import { Paragraph1 } from 'inpock-ui-react';
import dayjs from 'dayjs';

import { OrdersDetailResponse } from '@/apis';
import { OrderInfoLine } from '@/components/checkout';

import * as S from './styled';

interface ExtendedOrdersDetailResponse extends OrdersDetailResponse {
  index?: number;
}

interface CashReceiptInfoSectionProps {
  cashReceipt: ExtendedOrdersDetailResponse | null;
  section: 'buyer' | 'seller' | 'amount' | 'total';
}

const CASH_RECEIPT_INFO_TITLE: Record<string, string> = {
  buyer: '구매자 정보',
  seller: '판매자 정보',
  amount: '금액',
} as const;

// 발행 방법
const decideIssueMethod = (cash_receipt_type: string, identity_number: string) => {
  if (['personal_phone', 'personal_card'].includes(cash_receipt_type)) {
    return '소득공제';
  }
  if (cash_receipt_type === 'business_num') {
    return '지출증빙';
  }
  if (identity_number === '0100001234') {
    return '자진발급';
  }
  return '소득공제';
};

// 신청 구분
const CASH_RECEIPT_TYPE: Record<string, string> = {
  personal_phone: '휴대폰번호',
  personal_card: '카드번호',
  business_num: '사업자번호',
} as const;

function Info({ cashReceipt, section }: CashReceiptInfoSectionProps) {
  const { brand_info, cash_receipts } = cashReceipt?.cash_receipt_info || {};
  const { index } = cashReceipt || { index: 0 };
  const currentCashReceipt = cash_receipts?.[index ?? 0];

  if (section === 'buyer') {
    return (
      <>
        <OrderInfoLine
          label="국세청 승인번호"
          content={currentCashReceipt?.confirm_number ?? ''}
          valueColor="orange"
        />
        <OrderInfoLine
          label="구매자 발행번호"
          content={currentCashReceipt?.identity_number ?? ''}
        />
        <OrderInfoLine
          label="발행방법"
          content={decideIssueMethod(
            currentCashReceipt?.cash_receipt_type ?? '',
            currentCashReceipt?.identity_number ?? '',
          )}
        />
        <OrderInfoLine
          label="신청구분"
          content={CASH_RECEIPT_TYPE[currentCashReceipt?.cash_receipt_type ?? '']}
        />
        <OrderInfoLine
          label={currentCashReceipt?.trade_type === 'cancel' ? '취소일자' : '발행일자'}
          content={dayjs(currentCashReceipt?.created_at ?? '').format('YYYY.MM.DD HH:mm')}
        />
        <OrderInfoLine label="상품명" content={cashReceipt?.campaign_name ?? ''} />
        <OrderInfoLine label="주문번호" content={cashReceipt?.order_group_number ?? ''} />
      </>
    );
  }

  if (section === 'seller') {
    return (
      <>
        <OrderInfoLine label="판매자상호" content={brand_info?.company_name ?? ''} />
        <OrderInfoLine label="대표자명" content={brand_info?.ceo_name ?? ''} />
        <OrderInfoLine label="사업자등록번호" content={brand_info?.business_number ?? ''} />
        <OrderInfoLine label="전화번호" content={brand_info?.tel ?? ''} />
        <OrderInfoLine label="사업장주소" content={brand_info?.location ?? ''} />
      </>
    );
  }

  if (section === 'amount') {
    return (
      <>
        <OrderInfoLine
          label="공급가액"
          content={currentCashReceipt?.supply_amount?.toLocaleString() ?? ''}
        />
        <OrderInfoLine
          label="부가세액"
          content={currentCashReceipt?.tax_amount?.toLocaleString() ?? ''}
        />
      </>
    );
  }

  return (
    <S.TotalAmount>
      <Paragraph1>합계</Paragraph1>
      <Paragraph1 className="text-orange">
        {currentCashReceipt?.amount?.toLocaleString() ?? ''}
      </Paragraph1>
    </S.TotalAmount>
  );
}

export function CashReceiptInfoSection({ section, cashReceipt }: CashReceiptInfoSectionProps) {
  return (
    <S.Container>
      {section !== 'total' && (
        <S.TitleContainer>
          <S.Title>
            <Paragraph1>{CASH_RECEIPT_INFO_TITLE[section]}</Paragraph1>
          </S.Title>
        </S.TitleContainer>
      )}
      <div className="cash-receipt-info">
        {cashReceipt && <Info cashReceipt={cashReceipt} section={section} />}
      </div>
    </S.Container>
  );
}
